'use client';

import cx from 'classix';
import { useTranslations } from 'next-intl';

import ListingTypesBoxes from '@/features/listing/components/listing-types-boxes/listing-types-boxes';
import { DEFAULT_LISTING_PART_VARIANTS } from '@/features/listing/constants/listing-constants';
import { type ListingType } from '@/features/listing/types/listing-types';
import {
  type SitemapsPlaces,
  SitemapsPlacesLevel,
} from '@/features/place/types/place-types';
import { Link } from '@/i18n/routing';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import Pagination from '@/ui/pagination/pagination';

import styles from './sitemaps.module.scss';

interface SitemapsProps {
  placesType?: 'destinations' | 'neighborhoods',
  placesData: SitemapsPlaces,
  listingType: ListingType,
}

export default function Sitemaps(props: SitemapsProps) {
  const {
    listingType,
    placesData,
    placesType,
  } = props;

  const linksTranslations = useTranslations('global.links');
  const placeLevelTranslations = useTranslations('place.place-level');

  return (
    <>
      <div className={styles.sitemaps_placesLevel}>
        {placesType === 'destinations' ? (
          <p
            className={cx(
              styles.sitemaps_placeLevelItem,
              styles.sitemaps_placeLevelItem__selected,
            )}
          >
            {linksTranslations('destinations')}
          </p>
        ) : (
          // eslint-disable-next-line canonical/prefer-react-lazy
          <Link
            href={ROUTER_LINKS.sitemap}
            className={styles.sitemaps_placeLevelItem}
          >
            {linksTranslations('destinations')}
          </Link>
        )}

        {placesType === 'neighborhoods' ? (
          <p
            className={cx(
              styles.sitemaps_placeLevelItem,
              styles.sitemaps_placeLevelItem__selected,
            )}
          >
            {linksTranslations('neighborhoods')}
          </p>
        ) : (
          // eslint-disable-next-line canonical/prefer-react-lazy
          <Link
            href={ROUTER_LINKS.sitemapNeighborhoods}
            className={styles.sitemaps_placeLevelItem}
          >
            {linksTranslations('neighborhoods')}
          </Link>
        )}
      </div>

      <h1 className={styles.sitemaps_title}>
        {placesType ? linksTranslations(placesType) : placeLevelTranslations('cities')}
      </h1>

      <ListingTypesBoxes
        listingType={listingType}
      />

      <h2 className={styles.sitemaps_subtitle}>
        {placesData.level === SitemapsPlacesLevel.Countries ? placeLevelTranslations(placesData.level) : placesData.countryName}
      </h2>

      <ul className={styles.sitemaps_places}>
        {placesData.level === SitemapsPlacesLevel.Countries ? (
          placesData.items.map((place) => (
            <li key={place}>
              {/* eslint-disable-next-line canonical/prefer-react-lazy */}
              <Link
                className={styles.sitemaps_placeItem}
                href={`/sitemaps/destinations/${place.replaceAll(' ', '-')}`}
              >
                {place}
              </Link>
            </li>
          ))
        ) : (
          placesData.items.map((place) => {
            let cityUrlPart = place.loc;

            // Remove duplicate of city name from location
            // because geocode.earth don't give with city name duplicate
            // Example:v13th-arrondissement-of-Marseille--Marseille--France should be "13th-arrondissement-of-Marseille--France"
            // Was founded only in France, but can be more
            if (placesData.countryName === 'France' && cityUrlPart.includes('of')) {
              const cityTokens = cityUrlPart.split('--');

              if (cityTokens.length === 3) {
                const [
                  area,
                  region,
                  country,
                ] = cityTokens;

                if (area.includes(region)) {
                  cityUrlPart = `${area}--${country}`;
                }
              }
            }

            cityUrlPart = cityUrlPart.replaceAll('--', '-')
              .replaceAll(' ', '-')
              .replaceAll('~', '-')
              .toLowerCase();

            return (
              <li key={place.loc}>
                {/* eslint-disable-next-line canonical/prefer-react-lazy */}
                <Link
                  className={styles.sitemaps_placeItem}
                  href={`/${DEFAULT_LISTING_PART_VARIANTS[listingType]}/${cityUrlPart}`}
                >
                  {place.title}
                </Link>
              </li>
            );
          })
        )}
      </ul>

      {placesData.level === SitemapsPlacesLevel.Countries ? (
        <Pagination
          itemsCount={placesData.count}
          itemsPerPage={placesData.pageSize}
          pageNumberParamName="page_number"
        />
      ) : (
        <Pagination
          itemsCount={placesData.count}
          itemsPerPage={placesData.pageSize}
          pageNumberParamName="page_number"
        />
      )}
    </>
  );
}
